<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import SliderButton from '@admin-partials/SliderButton.vue';
import DeskCollapse from './DeskCollapse.vue';
import { ASSIST_THINKING_LABELS, pickRandomThinkingLabel } from '../assistThinkingLabels.js';

const props = defineProps({
    text: { type: String, default: '' },
    messageType: { type: String, default: 'reply' },
    instructions: { type: String, default: '' },
    composer: { type: Object, default: () => ({}) },
    labels: { type: Object, default: () => ({}) },
    ticketLabels: { type: Object, default: () => ({}) },
    assistBusy: { type: Boolean, default: false },
    assistAction: { type: String, default: '' },
    assistEnabled: { type: Boolean, default: true },
    agentChat: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
    'update:instructions',
    'ricrea',
    'crea-bozza',
    'stop-assist',
]);

const useMcpTools = ref(true);
const assistExpanded = ref(false);
const thinkingLabel = ref('');
const thinkingTimer = ref(null);
const labelPool = ref(ASSIST_THINKING_LABELS.slice());

const hasComposerText = computed(() => Boolean(props.text.trim()));
const hasAssistInstructions = computed(() => Boolean(props.instructions.trim()));

const formatProviderLabel = (provider) => {
    if (provider === 'openai') {
        return 'OpenAI';
    }
    if (provider === 'anthropic') {
        return 'Anthropic';
    }
    return provider;
};

const channelLlmHint = computed(() => {
    const provider = props.agentChat?.provider || 'openai';
    const model = props.agentChat?.model || props.agentChat?.default_model || '';
    const providerLabel = formatProviderLabel(provider);
    if (model) {
        return `${providerLabel} · ${model}`;
    }
    return providerLabel;
});

const channelLlmTitle = computed(() => (
    props.labels.assistOnChannel
    || props.labels.assist_on_channel
    || 'Impostato sul canale'
));

const mcpHelp = computed(() => (
    props.labels.assistUseMcpToolsHelp
    || props.labels.assist_use_mcp_tools_help
    || 'Se disattivato, Crea e Ricrea usano solo le tue istruzioni (e la bozza, se c’è), senza cercare ordini o prodotti.'
));

const stopThinking = () => {
    if (thinkingTimer.value) {
        window.clearInterval(thinkingTimer.value);
        thinkingTimer.value = null;
    }
    thinkingLabel.value = '';
};

const tickThinkingLabel = () => {
    const next = pickRandomThinkingLabel(labelPool.value, thinkingLabel.value);
    thinkingLabel.value = next;
    const remaining = labelPool.value.filter((label) => label !== next);
    if (remaining.length === 0) {
        labelPool.value = ASSIST_THINKING_LABELS.slice();
    } else {
        labelPool.value = remaining;
    }
};

const startThinking = () => {
    stopThinking();
    labelPool.value = ASSIST_THINKING_LABELS.slice();
    tickThinkingLabel();
    thinkingTimer.value = window.setInterval(tickThinkingLabel, 1600);
};

watch(
    () => props.assistBusy,
    (busy) => {
        if (busy) {
            startThinking();
        } else {
            stopThinking();
        }
    },
    { immediate: true },
);

onBeforeUnmount(() => {
    stopThinking();
});
</script>

<template>
    <div
        v-if="assistEnabled && composer.can_send_customer_reply !== false"
        class="ticket-desk-panel ticket-desk-writing-assistant"
        :class="{ 'ticket-desk-panel--collapsed': !assistExpanded }"
    >
        <button
            type="button"
            class="ticket-desk-panel-heading ticket-desk-panel-heading--toggle"
            :aria-expanded="assistExpanded"
            data-testid="ticket-desk-writing-assistant-toggle"
            @click="assistExpanded = !assistExpanded"
        >
            <h3>
                <span aria-hidden="true">✦</span>
                {{ labels.writingAssistant || 'Assistente di scrittura' }}
            </h3>
            <span class="ticket-desk-panel-heading__side">
                <span
                    class="ticket-desk-writing-assistant__llm-hint"
                    data-testid="ticket-ai-assist-llm-readonly"
                    :title="channelLlmTitle"
                >{{ channelLlmHint }}</span>
                <span class="ticket-desk-chevron" aria-hidden="true">▾</span>
            </span>
        </button>
        <DeskCollapse :expanded="assistExpanded">
            <div class="ticket-desk-writing-assistant__body">
                <textarea
                    id="ticket-ai-assist-body"
                    :value="instructions"
                    :placeholder="ticketLabels.aiInstructions || 'Istruzioni per l’AI (obbligatorie per Crea / Ricrea)…'"
                    :disabled="assistBusy"
                    @input="emit('update:instructions', $event.target.value)"
                />
                <div class="ticket-desk-writing-assistant__actions">
                    <button
                        v-if="assistBusy"
                        type="button"
                        class="ticket-desk-button ticket-desk-button--outline"
                        data-testid="ticket-assist-stop"
                        @click="emit('stop-assist')"
                    >
                        {{ labels.assist_stop || labels.stop || 'Stop' }}
                    </button>
                    <div class="ticket-desk-writing-assistant__ricrea-group">
                        <button
                            v-if="!hasComposerText"
                            type="button"
                            class="ticket-desk-button ticket-desk-button--primary"
                            :class="{ 'is-thinking': assistBusy && assistAction === 'crea' }"
                            data-testid="ticket-create-draft-with-ai"
                            :disabled="assistBusy || !hasAssistInstructions"
                            @click="emit('crea-bozza', { text, instructions, type: messageType, useMcpTools: useMcpTools })"
                        >
                            <template v-if="assistBusy && assistAction === 'crea'">
                                {{ thinkingLabel || 'Pensando' }}<span class="ticket-desk-thinking-dots" aria-hidden="true">…</span>
                            </template>
                            <template v-else>
                                ✎ {{ labels.createDraft || labels.crea_bozza || 'Crea bozza' }}
                            </template>
                        </button>
                        <button
                            v-if="hasComposerText"
                            type="button"
                            class="ticket-desk-button ticket-desk-button--primary"
                            :class="{ 'is-thinking': assistBusy && assistAction === 'ricrea' }"
                            data-testid="ticket-complete-with-ai"
                            :disabled="assistBusy || !hasAssistInstructions"
                            @click="emit('ricrea', { text, instructions, type: messageType, useMcpTools: useMcpTools })"
                        >
                            <template v-if="assistBusy && assistAction === 'ricrea'">
                                {{ thinkingLabel || 'Pensando' }}<span class="ticket-desk-thinking-dots" aria-hidden="true">…</span>
                            </template>
                            <template v-else>
                                ↻ {{ labels.regenerate || labels.ricrea || 'Ricrea' }}
                            </template>
                        </button>
                        <div
                            class="ticket-desk-writing-assistant__mcp-toggle"
                            data-testid="ticket-assist-use-mcp-tools"
                        >
                            <SliderButton
                                v-model="useMcpTools"
                                input-id="ticket-assist-use-mcp-tools"
                                :label="labels.assistUseMcpTools || labels.assist_use_mcp_tools || 'Consulta ordini e prodotti'"
                                :disabled="assistBusy"
                            />
                            <span
                                class="ticket-desk-writing-assistant__mcp-hint"
                                tabindex="0"
                                :data-tooltip="mcpHelp"
                                :aria-label="mcpHelp"
                            >?</span>
                        </div>
                    </div>
                </div>
            </div>
        </DeskCollapse>
    </div>
</template>
